import React from "react";

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Required when the switch has no adjacent text label (e.g. the icon picker's header). */
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Off-state track color. Defaults to the card context (edit popovers); the theme panel sits on
   * the main background and passes its own.
   */
  offTrackClassName?: string;
}

/**
 * Pill switch shared by the manual block editors (edit popover rows, icon picker header).
 * Geometry matches the Figma edit popover: a 28x16 track with a 12px knob inset 2px.
 *
 * Sized in absolute px, NOT rem utilities: the dashboard iframe sets a 14px root
 * (`font-size: 0.875rem` on <html>), so `w-7`/`h-4`/`w-3` would render at 87.5% while the
 * popover's own width stays an absolute 280px. The knob still travels its own width via
 * `translate-x-full` — 28 - 2x2 padding leaves 24, exactly two knob widths — so the travel can
 * never disagree with the track the way a hardcoded px offset did.
 *
 * Not `components/Switch`: that one is an AntD switch styled with styled-components and a
 * hardcoded brand blue, so it neither themes with the dashboard nor survives the iframe portal
 * (which has no StyleSheetManager).
 */
export const ToggleSwitch: React.FC<Props> = ({
  checked,
  onChange,
  ariaLabel,
  disabled = false,
  offTrackClassName = "bg-card-foreground/20",
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`inline-flex items-center h-[16px] w-[28px] shrink-0 rounded-full p-[2px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background ${
      checked ? "bg-primary-button-background" : offTrackClassName
    } ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
  >
    <span
      className={`block w-[12px] h-[12px] bg-white rounded-full shadow-sm transition-transform ${
        checked ? "translate-x-full" : "translate-x-0"
      }`}
    />
  </button>
);
